import type { StoredTokens, TokenStore } from "./token-store";

/** Paths that must never carry a bearer token or trigger a refresh retry. */
const AUTH_PATHS = [
  "/api/v1/auth/login",
  "/api/v1/auth/register",
  "/api/v1/auth/refresh",
  "/api/v1/auth/logout",
  "/api/v1/auth/forgot-password",
  "/api/v1/auth/reset-password",
  "/api/v1/auth/registration-options",
];

export interface AuthFetchOptions {
  baseUrl: string;
  store: TokenStore;
  /** Called when the session is gone for good, so the shell can send the breeder to sign in. */
  onSessionLost: () => void;
}

function isAuthPath(url: string): boolean {
  return AUTH_PATHS.some((path) => url.includes(path));
}

/**
 * A `fetch` that attaches the access token and, on a 401, attempts exactly one silent
 * refresh before giving up.
 *
 * Built as a fetch wrapper rather than as response middleware because the retry needs the
 * original request *before* its body is consumed. By the time a middleware sees the
 * response, the body stream is spent and a POST cannot be replayed — so the request is
 * cloned here, ahead of the first send.
 *
 * "Exactly one" matters in both directions: without a retry every expired token surfaces
 * as a spurious sign-out, and without the limit a server that answers 401 to everything
 * turns into an infinite refresh loop.
 */
export function createAuthFetch(options: AuthFetchOptions): typeof globalThis.fetch {
  const { store, onSessionLost } = options;

  // Shared across concurrent callers: a screen that fires five queries at once on load
  // must produce one refresh, not five. The later four await the same promise, and the
  // four extra rotations would have invalidated each other anyway.
  let inFlightRefresh: Promise<StoredTokens | null> | null = null;

  async function refreshOnce(): Promise<StoredTokens | null> {
    inFlightRefresh ??= (async () => {
      try {
        const current = store.read();
        if (!current?.refreshToken) {
          return null;
        }

        const response = await globalThis.fetch(`${options.baseUrl}/api/v1/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken: current.refreshToken, device: describeDevice() }),
        });

        if (!response.ok) {
          return null;
        }

        const body = (await response.json()) as { tokens: StoredTokens | null };
        if (!body.tokens) {
          return null;
        }

        store.write(body.tokens);
        return body.tokens;
      } catch {
        // Offline, or the API is down. Treated as a failed refresh rather than a lost
        // session — the caller still gets its 401 and can retry when the signal returns.
        return null;
      } finally {
        // Cleared in a microtask so callers that joined this refresh all observe it.
        queueMicrotask(() => {
          inFlightRefresh = null;
        });
      }
    })();

    return inFlightRefresh;
  }

  function withToken(request: Request, accessToken: string | undefined): Request {
    if (!accessToken) {
      return request;
    }

    const authorised = new Request(request);
    authorised.headers.set("Authorization", `Bearer ${accessToken}`);
    return authorised;
  }

  return async (input, init) => {
    const original = new Request(input as RequestInfo, init);

    // Cloned before the first send, because sending consumes the body.
    const retryable = original.clone();

    if (isAuthPath(original.url)) {
      return globalThis.fetch(original);
    }

    const response = await globalThis.fetch(withToken(original, store.read()?.accessToken));

    if (response.status !== 401) {
      return response;
    }

    const refreshed = await refreshOnce();

    if (!refreshed) {
      // One attempt, and it failed. Drop the credentials and let the shell redirect —
      // leaving them in place would have every subsequent request retry the same way.
      store.clear();
      onSessionLost();
      return response;
    }

    return globalThis.fetch(withToken(retryable, refreshed.accessToken));
  };
}

/** A short client hint for the sessions list. Best-effort — it is a label, not identity. */
export function describeDevice(): string | undefined {
  const agent = globalThis.navigator?.userAgent;
  return agent ? agent.slice(0, 120) : undefined;
}
